import { Location, LocationDescriptorObject } from 'history';
import * as queryString from 'querystring';
import { SheEnv } from '../../commonv3/utils/envs';
import { ReservationId } from '../../adminv3/types/session';

export type EnhancedLocationDescriptor = LocationDescriptorObject & {
  stringify(): string;
  toAbsolute(): string;
};

function v3Path(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  return {
    pathname: `/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

const DynamicPath = {
  openEventOverlay(current: Location, eventId: number): LocationDescriptorObject {
    const { search } = current;
    const query = search.startsWith('?') ? search.slice(1) : search;
    return {
      ...current,
      search: queryString.stringify({ ...queryString.parse(query), event_id: eventId.toString() }),
    };
  },
  closeEventOverlay(current: Location): LocationDescriptorObject {
    const { search } = current;
    const query = queryString.parse(search.startsWith('?') ? search.slice(1) : search);
    delete query.event_id;
    return {
      ...current,
      search: queryString.stringify(query),
    };
  },
};

export const V3Routes = {
  DynamicPath,
  moneyApplication() {
    return v3Path(`apply/money`);
  },
  applicationUserInfo() {
    return v3Path('application/user_info');
  },
  home() {
    return v3Path('home');
  },
  payments() {
    return v3Path('myaccount/payments');
  },
  retry_payment(code: string) {
    return v3Path(`home/mypage/retry_payment/${code}`);
  },
  generate_receipt(code: string) {
    return v3Path(`home/mypage/payments/${code}/receipt`);
  },
  reservations() {
    return v3Path('home/mypage/reservations');
  },
  likesHome() {
    if (SheEnv.isProduction()) {
      return 'https://shelikes.jp';
    }
    if (SheEnv.isStaging()) {
      return 'https://staging.shelikes.jp';
    }
    return 'http://localhost:3000';
  },
  likesUrl(path: string) {
    if (SheEnv.isProduction()) {
      return `https://shelikes.jp${path}`;
    }
    if (SheEnv.isStaging()) {
      return `https://staging.shelikes.jp${path}`;
    }
    return `http://localhost:3000${path}`;
  },
  signOutUrl() {
    if (SheEnv.isProduction() || SheEnv.isStaging()) {
      return V3Routes.likesUrl('/auth/sign_out_account_page');
    }
    return '/auth/sign_out_account_page';
  },
};

function beautyPath(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  let basePath = 'http://localhost:4001';
  if (SheEnv.isProduction()) {
    basePath = 'https://beauty.shelikes.jp';
  }
  if (SheEnv.isStaging()) {
    basePath = 'https://staging.beauty.shelikes.jp';
  }
  return {
    pathname: `${basePath}/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

export const BeautyRoutes = {
  trialLessons() {
    return beautyPath(`trial_lessons`);
  },
  trialLessonReschedule(id: ReservationId) {
    return beautyPath(`trial_lessons/${id.toString()}/reschedule`);
  },
  home() {
    return beautyPath('');
  },
  memberHome() {
    return beautyPath('home');
  },
};

function moneyPath(path: string, params: Partial<LocationDescriptorObject> = {}): EnhancedLocationDescriptor {
  let basePath = 'http://localhost:5001';
  if (SheEnv.isProduction()) {
    basePath = 'https://money.shelikes.jp';
  }
  if (SheEnv.isStaging()) {
    basePath = 'https://staging.money.shelikes.jp';
  }
  return {
    pathname: `${basePath}/${path}`,
    stringify() {
      const search = this.search ? `?${this.search}` : '';
      const hash = this.hash ? this.hash : '';
      return `/${path}${hash}${search}`;
    },
    toAbsolute() {
      return `${location.origin}${this.stringify()}`;
    },
    ...params,
  };
}

export const MoneyRoutes = {
  trialLessons() {
    return moneyPath(`trial_lessons`);
  },
  home() {
    return moneyPath('');
  },
  memberHome() {
    return moneyPath('home');
  },
};

/**
 * FAQ の URL
 */
export const FAQ_URL = 'https://docs.channel.io/shemoney/ja';
